import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Grid, Stack, Typography, IconButton, Button, LinearProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import {
  ArrowBack, Assignment, AccountBalanceWallet, ReceiptLong, Payments, Percent, TrendingUp,
} from '@mui/icons-material';
import { api } from '../../services/apiClient';
import { formatIndianNumber } from '../../utils/currency';
import { useControlRoomColors } from '../../components/common/controlRoom';
import {
  PageHeader,
  StatCard,
  TableSurface,
  StatusDot,
  StatusTone,
  headerCellSx,
  bodyRowSx,
  TableEmptyState,
} from '../../components/common/pageKit';

interface VendorProfile {
  id: number | string;
  name: string;
  gstin: string | null;
  pan: string | null;
  type: string | null;
}

interface VendorSummary {
  pos: number; poValue: number; bills: number; billed: number; paid: number; payable: number; tds: number;
}

interface InvoiceRow { ref: string; date: string; amount: number; status: string; }
interface PaymentRow { ref: string; date: string; gross: number; tds: number; net: number; }
interface TimelineRow { ts: string; kind: string; label: string; }

interface VendorDetail {
  profile: VendorProfile;
  summary: VendorSummary;
  invoices: InvoiceRow[];
  payments: PaymentRow[];
  gst: { tax: number };
  timeline: TimelineRow[];
}

const KIND_TINTS = ['#dc2626', '#0284c7', '#7c3aed', '#d97706', '#059669', '#64748b'];

const VendorDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const C = useControlRoomColors();
  const [d, setD] = useState<VendorDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(null);
    setNotFound(false);
    api.get<VendorDetail>(`/procurement-analytics/supplier-360/${id}`)
      .then(setD)
      .catch((e: any) => {
        if (e?.response?.status === 404 || e?.status === 404) setNotFound(true);
        else setError(e?.message || 'Failed to load vendor');
      })
      .finally(() => setLoading(false));
  }, [id]);

  // Kind-coloured timeline dots. Kind values come from the backend as free
  // text, so this hashes to a stable colour rather than requiring an exact
  // enum match.
  const kindDot = (kind: string): string => {
    let hash = 0;
    for (let i = 0; i < kind.length; i += 1) hash = (hash * 31 + kind.charCodeAt(i)) % KIND_TINTS.length;
    return KIND_TINTS[Math.abs(hash) % KIND_TINTS.length];
  };

  const invoiceStatusTone = (status: string): StatusTone => {
    const s = (status || '').toLowerCase();
    if (s.includes('paid') || s.includes('cleared') || s.includes('complete')) return 'success';
    if (s.includes('overdue') || s.includes('reject') || s.includes('fail')) return 'error';
    if (s.includes('pending') || s.includes('open') || s.includes('due') || s.includes('partial')) return 'warning';
    return 'neutral';
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (notFound) {
    return (
      <Box sx={{ p: 3 }}>
        <IconButton onClick={() => navigate('/procurement/vendors')} sx={{ mb: 1 }}><ArrowBack /></IconButton>
        <Alert severity="warning">Vendor not found. It may have been removed or the link is out of date.</Alert>
        <Button sx={{ mt: 2 }} variant="outlined" onClick={() => navigate('/procurement/vendors')}>Back to Vendors</Button>
      </Box>
    );
  }

  if (error || !d) {
    return (
      <Box sx={{ p: 3 }}>
        <IconButton onClick={() => navigate('/procurement/vendors')} sx={{ mb: 1 }}><ArrowBack /></IconButton>
        <Alert severity="error">{error || 'Unable to load this vendor.'}</Alert>
      </Box>
    );
  }

  const { profile, summary, invoices, payments, gst, timeline } = d;
  const subtitleParts: string[] = [];
  if (profile.gstin) subtitleParts.push(`GSTIN ${profile.gstin}`);
  if (profile.pan) subtitleParts.push(`PAN ${profile.pan}`);
  if (profile.type) subtitleParts.push(profile.type);

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="flex-start" spacing={1.5}>
        <IconButton onClick={() => navigate('/procurement/vendors')} sx={{ mt: 0.5 }}>
          <ArrowBack />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <PageHeader
            title={profile.name}
            subtitle={subtitleParts.join(' · ') || undefined}
            actions={<Button variant="outlined" onClick={() => navigate('/procurement/payments')}>Vendor Payments</Button>}
          />
        </Box>
      </Stack>

      <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
        <Grid size={{ xs: 6, sm: 4, md: 3 }}>
          <StatCard title="Open POs" value={summary.pos ?? 0} icon={<Assignment />} color={C.accent} />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 3 }}>
          <StatCard title="PO Value" value={formatIndianNumber(summary.poValue || 0)} icon={<AccountBalanceWallet />} color={C.accent} />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 3 }}>
          <StatCard title="Billed" value={formatIndianNumber(summary.billed || 0)} icon={<ReceiptLong />} color={C.cyan} footer={<Typography variant="caption" color="text.secondary">{summary.bills || 0} bills</Typography>} />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 3 }}>
          <StatCard title="Paid" value={formatIndianNumber(summary.paid || 0)} icon={<Payments />} color={C.good} />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 3 }}>
          <StatCard title="Payable" value={formatIndianNumber(summary.payable || 0)} icon={<TrendingUp />} color={(summary.payable || 0) > 0 ? C.bad : C.text} />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 3 }}>
          <StatCard title="TDS Deducted" value={formatIndianNumber(summary.tds || 0)} icon={<Percent />} color={C.purple} onClick={() => navigate('/finance/tds-reports')} />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 3 }}>
          <StatCard title="GST ITC" value={formatIndianNumber(gst?.tax || 0)} icon={<Percent />} color={C.cyan} onClick={() => navigate('/finance/gst-reports')} />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Purchase Invoices</Typography>
          <TableSurface>
            <TableContainer sx={{ maxHeight: 320, overflowX: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>Invoice</TableCell>
                    <TableCell sx={headerCellSx}>Date</TableCell>
                    <TableCell sx={headerCellSx} align="right">Amount</TableCell>
                    <TableCell sx={headerCellSx}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices.map((inv, idx) => (
                    <TableRow
                      key={idx}
                      hover
                      sx={{ ...bodyRowSx, cursor: 'pointer' }}
                      onClick={() => navigate('/procurement/invoices')}
                    >
                      <TableCell sx={{ fontFamily: 'monospace' }}>{inv.ref}</TableCell>
                      <TableCell>{String(inv.date).slice(0, 10)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatIndianNumber(inv.amount)}</TableCell>
                      <TableCell><StatusDot label={inv.status} tone={invoiceStatusTone(inv.status)} /></TableCell>
                    </TableRow>
                  ))}
                  {!invoices.length && (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ border: 0 }}>
                        <TableEmptyState title="No purchase invoices yet" />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TableSurface>

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, mt: 2.5 }}>Payments</Typography>
          <TableSurface>
            <TableContainer sx={{ maxHeight: 320, overflowX: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>Payment</TableCell>
                    <TableCell sx={headerCellSx}>Date</TableCell>
                    <TableCell sx={headerCellSx} align="right">Gross</TableCell>
                    <TableCell sx={headerCellSx} align="right">TDS</TableCell>
                    <TableCell sx={headerCellSx} align="right">Net</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payments.map((p, idx) => (
                    <TableRow
                      key={idx}
                      hover
                      sx={{ ...bodyRowSx, cursor: 'pointer' }}
                      onClick={() => navigate('/procurement/payments')}
                    >
                      <TableCell sx={{ fontFamily: 'monospace' }}>{p.ref}</TableCell>
                      <TableCell>{String(p.date).slice(0, 10)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatIndianNumber(p.gross)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatIndianNumber(p.tds)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{formatIndianNumber(p.net)}</TableCell>
                    </TableRow>
                  ))}
                  {!payments.length && (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ border: 0 }}>
                        <TableEmptyState title="No payments yet" />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TableSurface>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Vendor Activity</Typography>
          <Box sx={{ bgcolor: C.panel, border: `1px solid ${C.line}`, borderRadius: 2, p: 2 }}>
            <Box sx={{ position: 'relative', pl: 2, '&:before': { content: '""', position: 'absolute', left: 5, top: 4, bottom: 4, width: 2, bgcolor: C.line } }}>
              {timeline.map((t, i) => (
                <Box key={i} sx={{ position: 'relative', pb: 1.5 }}>
                  <Box sx={{ position: 'absolute', left: -15, top: 4, width: 10, height: 10, borderRadius: '50%', bgcolor: kindDot(t.kind), boxShadow: `0 0 6px ${kindDot(t.kind)}` }} />
                  <Typography sx={{ fontSize: 11, color: C.sub, fontFamily: 'monospace' }}>{String(t.ts).slice(0, 16).replace('T', ' ')}</Typography>
                  <Typography sx={{ fontSize: 13, color: C.text }}>{t.label}</Typography>
                </Box>
              ))}
              {!timeline.length && <Typography sx={{ color: C.sub, fontSize: 13 }}>No recent activity.</Typography>}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default VendorDetailPage;
